import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { component_identity } from "abreact/core/identity/IdentityUtils";
import { PageStateLogger } from "abreact/core/pagestate/PageStateLogger";
import { action, makeObservable, ObservableMap } from "mobx";

/**
 * Defines the structure that is serialized to string for storage in teh session
 */

/**
 * State store for a single component associated with this qualified id
 */

export let ComponentPageState = (_class = class ComponentPageState {
  constructor(component, qualifiedId, mobx) {
    _defineProperty(this, "_component", void 0);
    _defineProperty(this, "_qualifiedId", void 0);
    _defineProperty(this, "_keys", []);
    _defineProperty(this, "_useMobx", void 0);
    makeObservable(this);
    this._component = component;
    if (qualifiedId) {
      this._qualifiedId = qualifiedId;
    } else if (component?.[component_identity]?.qualifiedId) {
      this._qualifiedId = component[component_identity].qualifiedId;
    } else {
      throw new Error("Unable to create component page state storage. No qualifiedId provided or set on component.");
    }
    this._useMobx = !!mobx;
    this._observableValues = new ObservableMap();
    this._nonObservableValues = new Map();
    this._useMobx = !!mobx;
  }
  initValues(observableValues, nonObservableValues) {
    this.updateValues(observableValues, nonObservableValues);
  }
  get keys() {
    return this._keys;
  }
  updateValues(observableValues, nonObservableValues) {
    Object.keys(observableValues).forEach(key => {
      this.addKey(key);
      this._observableValues.set(key, observableValues[key]);
    });
    Object.keys(nonObservableValues).forEach(key => {
      this.addKey(key);
      this._nonObservableValues.set(key, nonObservableValues[key]);
    });
  }
  addKey(key) {
    if (this._keys.indexOf(key) === -1) {
      this._keys.push(key);
    }
  }
  get observableValues() {
    let result = {};
    let keys = Array.from(this._observableValues.keys());
    keys.forEach(key => {
      result[key] = this._observableValues.get(key);
    });
    return result;
  }
  get nonObservableValues() {
    let result = {};
    let keys = Array.from(this._nonObservableValues.keys());
    keys.forEach(key => {
      result[key] = this._nonObservableValues.get(key);
    });
    return result;
  }
  get qualifiedId() {
    return this._qualifiedId;
  }
  get component() {
    return this._component;
  }
  get(key) {
    this.addKey(key);
    this.logWarningOnIdentityIfPresent();
    let value;
    if (this._observableValues.has(key)) {
      value = this._observableValues.get(key);
      this.reportAction("get stored observable value", key, value);
    } else if (this._nonObservableValues.has(key)) {
      value = this._nonObservableValues.get(key);
      this.reportAction("get stored non-observable value", key, value);
    } else {
      this.reportAction("no value found for", key, value, value === undefined);
    }
    return value;
  }
  logWarningOnIdentityIfPresent() {
    if (this.component) {
      const componentIdentity = this.component[component_identity];
      if (componentIdentity.loggableWarning) {
        PageStateLogger.logWarning(componentIdentity.loggableWarning);
        // only log it once
        componentIdentity.loggableWarning = undefined;
      }
    }
  }
  set(key, value) {
    this.addKey(key);
    this.logWarningOnIdentityIfPresent();
    if (this._useMobx) {
      this.setObservableValue(key, value);
    } else {
      this.setSilent(key, value);
    }
  }
  setObservableValue(key, value) {
    this._observableValues.set(key, value);
    this.reportAction("set observable value", key, value);
  }
  setSilent(key, value) {
    this._nonObservableValues.set(key, value);
    this.reportAction("set silent value", key, value);
  }
  reportAction(methodName, key, value, omitValue) {
    let stringValue;
    if (!omitValue) {
      stringValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : value && value.toString();
    }
    PageStateLogger.maybeLog("state", `${methodName} for ${key}:${!omitValue ? " " + stringValue : ""} on ${this.qualifiedId ?? "unknown"}`);
    return value;
  }
  toJsonObject() {
    if (this._keys.length > 0) {
      return {
        qualifiedId: this.qualifiedId,
        observableValues: this.observableValues,
        nonObservableValues: this.nonObservableValues,
        useMobx: this._useMobx
      };
    } else {
      return null;
    }
  }
  static fromJsonObject(json) {
    const componentPageState = new ComponentPageState(null, json.qualifiedId, json.useMobx);
    componentPageState.initValues(json.observableValues, json.nonObservableValues);
    return componentPageState;
  }
}, _applyDecoratedDescriptor(_class.prototype, "updateValues", [action], Object.getOwnPropertyDescriptor(_class.prototype, "updateValues"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setObservableValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setObservableValue"), _class.prototype), _class);
//# sourceMappingURL=ComponentPageState.js.map
